import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../types/types';
import { improveWriting } from '../prebuilt/config-items/prompts/enhance/enhance';

const MIN_PASTE_CONTENT_SIZE = 200;
const MAX_PASTE_CONTENT_SIZE = 1200;

export const shouldTriggerOnPaste = (text: string | undefined): boolean => {
	// Only check text character count
	if (!text) {
		return false;
	}

	const charCount = text.length;
	return (
		charCount >= MIN_PASTE_CONTENT_SIZE &&
		charCount <= MAX_PASTE_CONTENT_SIZE &&
		expValEquals('cc_improve_writing_on_paste', 'isEnabled', true)
	);
};

export const handlePasteStateChange = ({
	pasteState,
	editorView,
	aiOptIn,
	locale,
}: {
	aiOptIn: AIGlobalOptIn;
	editorView: EditorView | undefined;
	locale: string;
	pasteState:
		| { lastContentPasted?: { pasteEndPos: number; pasteStartPos: number; text: string } | null }
		| undefined;
}) => {
	if (!pasteState?.lastContentPasted) {
		return;
	}

	const { lastContentPasted } = pasteState;

	// Check if the pasted content is eligible
	const shouldTrigger = shouldTriggerOnPaste(lastContentPasted.text);

	if (
		shouldTrigger &&
		editorView &&
		lastContentPasted.pasteStartPos !== undefined &&
		lastContentPasted.pasteEndPos !== undefined
	) {
		const tr = editorView.state.tr.setSelection(
			TextSelection.create(
				editorView.state.doc,
				lastContentPasted.pasteStartPos,
				lastContentPasted.pasteEndPos,
			),
		);
		editorView.dispatch(tr);

		// Create and execute the AI modal command for Improve Writing
		const openAIModalCommand = createOpenAIModalCommand({
			state: editorView.state,
			configItem: improveWriting(),
			lastTriggeredFrom: 'auto_paste',
			aiGlobalOptIn: aiOptIn,
			locale,
		});

		openAIModalCommand(editorView.state, editorView.dispatch);
	}
};
